import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import OwnerQuotationInvoiceView from "../components/OwnerQuotationInvoiceView";
import API_URL from '../api/baseURL';
import { Page, Panel, Badge, Table, Tr, Td, EmptyState, Loading, BackLink, Button } from '../components/ui/Iparc';
import { fmt } from '../theme/iparc';

// Backend statuses -> French label + signal tone (two-colour rule: red blocks,
// amber waits, green is settled).
const STATUS = {
  pending:   { label: 'En attente', tone: 'warn' },
  revised:   { label: 'Révisé',     tone: 'warn' },
  accepted:  { label: 'Accepté',    tone: 'ok' },
  confirmed: { label: 'Confirmé',   tone: 'ok' },
  rejected:  { label: 'Refusé',     tone: 'danger' },
};
const statusOf = (s) => STATUS[s] || { label: s || '—', tone: 'neutral' };

export default function OwnerQuotationsPage() {
  const { token } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/workshop-quotations/my`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Not allowed or error fetching');
        return res.json();
      })
      .then(data => setQuotations(Array.isArray(data) ? data : []))
      .catch(() => setQuotations([]))
      .finally(() => setLoading(false));
  }, [token]);

  const pendingCount = useMemo(
    () => quotations.filter(q => ['pending', 'revised'].includes(q.status)).length,
    [quotations]
  );

  const shown = useMemo(() => {
    if (filter === 'pending') return quotations.filter(q => ['pending', 'revised'].includes(q.status));
    if (filter === 'settled') return quotations.filter(q => ['accepted', 'confirmed'].includes(q.status));
    return quotations;
  }, [quotations, filter]);

  if (selected) {
    return (
      <Page width="narrow">
        <BackLink onClick={() => setSelected(null)} />
        <OwnerQuotationInvoiceView quotation={selected} />
      </Page>
    );
  }

  const chips = [
    { key: 'all', label: `Tous · ${quotations.length}` },
    { key: 'pending', label: `À répondre · ${pendingCount}` },
    { key: 'settled', label: `Acceptés · ${quotations.filter(q => ['accepted','confirmed'].includes(q.status)).length}` },
  ];

  return (
    <Page title="Mes devis" subtitle="Atelier">
      <Panel
        title="Devis reçus"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map(c => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {loading ? (
          <Loading label="Chargement des devis…" />
        ) : quotations.length === 0 ? (
          <EmptyState
            title="Aucun devis"
            hint="Les devis envoyés par vos ateliers apparaîtront ici."
            action={
              <Button variant="primary" onClick={() => navigate('/book-workshop')}>
                Demander un service
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucun devis dans cette vue." />
        ) : (
          <Table head={['Atelier', 'Véhicule', 'Total', 'Statut', '']} minWidth={760}>
            {shown.map(q => {
              const st = statusOf(q.status);
              return (
                <Tr key={q._id} onClick={() => setSelected(q)}>
                  <Td className="text-[#F4F1EA]">
                    {q.workshop?.businessName || q.workshop?.details?.businessName || q.workshop?.name || '—'}
                  </Td>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                      {q.vehicle?.plateNumber || '—'}
                    </span>
                  </Td>
                  <Td mono>
                    {fmt(q.clientTotal || q.total || 0)}{' '}
                    <span className="text-[10.5px] text-[#6E665C]">F</span>
                  </Td>
                  <Td><Badge tone={st.tone}>{st.label}</Badge></Td>
                  <Td className="text-right whitespace-nowrap">
                    {q.status === 'confirmed' && q.invoiceId && (
                      <Button
                        variant="quiet"
                        className="h-[26px] px-2 text-[#74A98D] hover:text-[#8FBFA4]"
                        onClick={(e) => { e.stopPropagation(); navigate(`/owner/invoice/${q.invoiceId}`); }}
                      >
                        Facture
                      </Button>
                    )}
                    <Button variant="quiet" className="h-[26px] px-2">Voir</Button>
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>
    </Page>
  );
}
